/**
 * Companies page - snapshot of diligence pipeline
 */
import React, { useEffect, useState } from 'react';
import { Building2, Loader2, FileText, Search, UploadCloud, X, Users } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/DashboardLayout';
import DualScoreChip from '../components/DualScoreChip';
import EntityTableView from '../components/EntityTableView';
import dataService, { CompanyRecord, PlatformStats } from '../services/data';
import api from '../services/api';
import capitalReadinessService from '../services/capitalReadinessService';
import momentumService from '../services/momentumService';
import { CapitalReadinessScore } from '../types/capitalReadiness';
import { DealMomentum } from '../types/dealMomentum';
import CRSTierBadge from '../components/CRSTierBadge';
import MomentumBadge from '../components/MomentumBadge';
import ManageGroupsModal from '../components/ManageGroupsModal';

// Freshness — see InvestorsPage for the same logic. Companies use the
// same age buckets so the operator can see at a glance whether a row
// is up to date with the current pipeline.
const STALE_DAYS = 14;
const VERY_STALE_DAYS = 30;

const freshnessFor = (lastResearchedAt?: string | null) => {
  if (!lastResearchedAt) {
    return { label: 'Not assessed', color: 'bg-gray-100 text-gray-700 border-gray-200' };
  }
  let ageDays = 0;
  try {
    ageDays = Math.floor(
      (Date.now() - new Date(lastResearchedAt).getTime()) / (1000 * 60 * 60 * 24),
    );
  } catch {
    ageDays = 999;
  }
  const dateStr = (() => {
    try { return new Date(lastResearchedAt).toLocaleDateString(); } catch { return ''; }
  })();
  if (ageDays >= VERY_STALE_DAYS) {
    return { label: `Assessed ${dateStr} · update needed`, color: 'bg-red-50 text-red-700 border-red-200' };
  }
  if (ageDays >= STALE_DAYS) {
    return { label: `Assessed ${dateStr} · ${ageDays}d old`, color: 'bg-amber-50 text-amber-700 border-amber-200' };
  }
  if (ageDays === 0) {
    return { label: 'Assessed today', color: 'bg-green-50 text-green-700 border-green-200' };
  }
  return { label: `Assessed ${ageDays}d ago`, color: 'bg-green-50 text-green-700 border-green-200' };
};

const formatDate = (value?: string) => {
  if (!value) return 'No analyses yet';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const CompaniesPage: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<CompanyRecord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [crsMap, setCrsMap] = useState<Record<number, CapitalReadinessScore>>({});
  const [momentumMap, setMomentumMap] = useState<Record<number, DealMomentum>>({});
  const [researchingId, setResearchingId] = useState<number | null>(null);
  const [bulkRunning, setBulkRunning] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [searchInput, setSearchInput] = useState<string>(searchQuery);
  const [platform, setPlatform] = useState<PlatformStats | null>(null);
  // By default, hide thinly-researched companies from the main list.
  // Search bypasses regardless. Toggle to see everything.
  const [showAll, setShowAll] = useState<boolean>(false);
  const MIN_COMPLETION_DEFAULT = 60;
  // Operator-selectable completion floor. null = use the page default
  // (or 0 when "Show all" is on). Lets the user list companies above
  // 70 / 80 / 90 % completion without round-tripping through search.
  const [minCompletion, setMinCompletion] = useState<number | null>(null);
  // Sort order. Default puts the most-researched companies on top so
  // the operator's eye lands on rows worth acting on.
  const [sort, setSort] = useState<string>('completion_desc');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>(
    (localStorage.getItem('companiesViewMode') as 'cards' | 'table') || 'cards',
  );
  useEffect(() => {
    localStorage.setItem('companiesViewMode', viewMode);
  }, [viewMode]);
  const [group, setGroup] = useState<string>(searchParams.get('group') || '');
  const [groupOptions, setGroupOptions] = useState<Array<{ name: string; count: number }>>([]);
  const [manageGroupsOpen, setManageGroupsOpen] = useState(false);
  const [minRevenue, setMinRevenue] = useState<number | null>(null);
  useEffect(() => {
    dataService.fetchCompanyGroups().then((res) => {
      if (res.data) setGroupOptions(res.data);
    });
  }, []);

  useEffect(() => {
    dataService.fetchPlatformStats().then((res) => {
      if (res.data) setPlatform(res.data);
    });
  }, []);

  // Keep the local input in sync if the URL param changes (e.g. global header navigates here)
  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  const triggerResearch = async (companyId: number, silent: boolean = false): Promise<boolean> => {
    try {
      const response = await api.post(`/companies/${companyId}/research`);
      if (!silent) toast.success('Research queued');
      return response.status >= 200 && response.status < 300;
    } catch (e: any) {
      if (!silent) toast.error(e?.response?.data?.message || 'Research trigger failed');
      return false;
    }
  };

  const handleResearchOne = async (companyId: number) => {
    setResearchingId(companyId);
    await triggerResearch(companyId);
    setResearchingId(null);
  };

  const handleResearchAll = async () => {
    if (companies.length === 0) return;
    if (!window.confirm(`Run deep-dive research on all ${companies.length} companies? This is rate-limited (~20 per 30 min batch).`)) {
      return;
    }
    setBulkRunning(true);
    let queued = 0;
    for (const company of companies) {
      const ok = await triggerResearch(company.id, true);
      if (ok) queued += 1;
    }
    setBulkRunning(false);
    toast.success(`Queued ${queued}/${companies.length} for research`);
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      // Group filter implicitly bypasses the research-completion gate.
      // Rationale: the user explicitly named the cohort they want to see
      // (an event roster, a partner batch, an imported list) — hiding
      // half of it behind a default 60% threshold contradicts that
      // intent and forces the "Show all" toggle every time.
      const effectiveMin =
        minCompletion !== null
          ? minCompletion
          : showAll || group
            ? 0
            : MIN_COMPLETION_DEFAULT;
      const result = await dataService.fetchCompanies({
        search: searchQuery || undefined,
        size: 200,
        min_completion: effectiveMin,
        sort,
        group: group || undefined,
        min_revenue: minRevenue ?? undefined,
      });
      if (result.error) {
        toast.error(result.error);
      } else {
        setCompanies(result.data || []);
      }
      setIsLoading(false);
    };

    loadData();
  }, [searchQuery, showAll, minCompletion, sort, group, minRevenue]);

  // Fetch CRS + momentum in background, don't block initial render
  useEffect(() => {
    if (companies.length === 0) return;
    let cancelled = false;
    (async () => {
      const [scoresRes, signalsRes] = await Promise.all([
        capitalReadinessService.fetchScores({ size: 500 }),
        momentumService.fetchSignals(),
      ]);
      if (cancelled) return;
      const cMap: Record<number, CapitalReadinessScore> = {};
      (scoresRes.data?.scores || []).forEach((s) => {
        if (s.startup_id) {
          const existing = cMap[s.startup_id];
          if (
            !existing ||
            new Date(s.created_at) > new Date(existing.created_at)
          ) {
            cMap[s.startup_id] = s;
          }
        }
      });
      setCrsMap(cMap);
      const mMap: Record<number, DealMomentum> = {};
      (signalsRes.data || []).forEach((m) => {
        if (m.startup_id) mMap[m.startup_id] = m;
      });
      setMomentumMap(mMap);
    })();
    return () => {
      cancelled = true;
    };
  }, [companies]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
            <p className="text-gray-600 mt-1">Keep tabs on every company that hit your deal room.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleResearchAll}
              disabled={bulkRunning || companies.length === 0}
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Trigger the deep-dive research pipeline on every company"
            >
              {bulkRunning ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Run Research on All
            </button>
            <Link
              to="/companies/duplicates"
              className="inline-flex items-center px-4 py-2 bg-white text-amber-700 border border-amber-300 rounded-lg font-medium hover:bg-amber-50 transition-colors"
              title="Find companies that look like duplicates and merge them"
            >
              Find duplicates
            </Link>
            <Link
              to="/companies/upload-brochure"
              className="inline-flex items-center px-4 py-2 bg-white text-gray-800 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              title="One uploader for pitch deck, brochure, or product catalog — same Claude Vision extraction creates a company record"
            >
              <UploadCloud className="w-4 h-4 mr-2" />
              Upload pitch deck / brochure
            </Link>
            <Link
              to="/companies/new"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              <FileText className="w-4 h-4 mr-2" />
              Add company
            </Link>
          </div>
        </div>

        {/* Stats card */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button
            type="button"
            onClick={() => { setSearchInput(''); setSearchParams({}); }}
            className="bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
            title="Show all companies (clears search)"
          >
            <p className="text-2xl font-bold text-gray-900">{platform?.companies.total ?? '—'}</p>
            <p className="text-xs text-gray-500 mt-1">Total companies</p>
          </button>
          <button
            type="button"
            onClick={() => { setSearchInput(''); setSearchParams({}); }}
            className="bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
            title="Researched companies (filtering by research status coming soon)"
          >
            <p className="text-2xl font-bold text-gray-900">{platform?.companies.researched ?? '—'}</p>
            <p className="text-xs text-gray-500 mt-1">
              Researched
              {platform && platform.companies.total > 0 && (
                <span className="ml-1 text-gray-400">
                  ({Math.round((platform.companies.researched / platform.companies.total) * 100)}%)
                </span>
              )}
            </p>
          </button>
          <Link
            to="/matches"
            className="bg-white rounded-xl border border-gray-200 p-4 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
            title="View all matches"
          >
            <p className="text-2xl font-bold text-gray-900">{platform?.companies.matched ?? '—'}</p>
            <p className="text-xs text-gray-500 mt-1">Matched to investors</p>
          </Link>
          <Link
            to="/deal-pipeline"
            className="bg-white rounded-xl border border-gray-200 p-4 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
            title="View deal pipeline"
          >
            <p className="text-2xl font-bold text-gray-900">{platform?.companies.with_deal ?? '—'}</p>
            <p className="text-xs text-gray-500 mt-1">In a deal</p>
          </Link>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            const q = searchInput.trim();
            if (q) setSearchParams({ search: q });
            else setSearchParams({});
          }}
          className="flex items-center flex-wrap gap-2 bg-white border border-gray-200 rounded-xl p-3"
        >
          <Search className="w-4 h-4 text-gray-400 ml-1" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by company name, contact, or description…"
            className="flex-1 bg-transparent text-sm focus:outline-none"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                setSearchParams({});
              }}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            type="submit"
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
          >
            Search
          </button>
          <label
            className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer whitespace-nowrap select-none ml-2"
            title={`Default view hides companies below ${MIN_COMPLETION_DEFAULT}% research completion. Search bypasses this gate.`}
          >
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              className="rounded"
            />
            Show all (incl. unassessed)
          </label>
          <select
            value={minCompletion ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setMinCompletion(v === '' ? null : Number(v));
            }}
            className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            title="Filter by completion score range"
          >
            <option value="">Any completion</option>
            <option value="0">≥ 0%</option>
            <option value="50">≥ 50%</option>
            <option value="60">≥ 60%</option>
            <option value="70">≥ 70%</option>
            <option value="80">≥ 80%</option>
            <option value="90">≥ 90%</option>
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            title="Sort the list"
          >
            <option value="completion_desc">Sort: Completion (high → low)</option>
            <option value="completion_asc">Sort: Completion (low → high)</option>
            <option value="name_asc">Sort: Name (A → Z)</option>
            <option value="name_desc">Sort: Name (Z → A)</option>
            <option value="researched_recent">Sort: Recently researched</option>
            <option value="revenue_desc">Sort: Revenue (high → low)</option>
            <option value="revenue_asc">Sort: Revenue (low → high)</option>
            <option value="raising_desc">Sort: Currently raising (high → low)</option>
            <option value="recent">Sort: Recently added</option>
            <option value="oldest">Sort: Oldest first</option>
          </select>
          <select
            value={minRevenue ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setMinRevenue(v === '' ? null : Number(v));
            }}
            className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            title="Filter by minimum annual revenue"
          >
            <option value="">Any revenue</option>
            <option value="100000">≥ $100K</option>
            <option value="500000">≥ $500K</option>
            <option value="1000000">≥ $1M</option>
            <option value="5000000">≥ $5M</option>
            <option value="10000000">≥ $10M</option>
          </select>
          <select
            value={group}
            onChange={(e) => {
              const v = e.target.value;
              setGroup(v);
              const next = new URLSearchParams(searchParams);
              if (v) next.set('group', v); else next.delete('group');
              setSearchParams(next);
            }}
            className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            title="Filter by group / partner / batch"
          >
            <option value="">All groups</option>
            {groupOptions.map((g) => (
              <option key={g.name} value={g.name}>
                {g.name} ({g.count})
              </option>
            ))}
          </select>
          <button
            onClick={() => setManageGroupsOpen(true)}
            className="inline-flex items-center gap-1 text-xs px-2 py-1.5 bg-white border border-gray-200 rounded-md hover:bg-gray-50"
            title="Create, rename, or disband company groups"
          >
            <Users className="w-3 h-3" />
            Manage groups
          </button>
          <div className="inline-flex items-center rounded-md border border-gray-200 bg-white overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setViewMode('cards')}
              className={`px-2.5 py-1.5 ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              title="Card view"
            >
              Cards
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`px-2.5 py-1.5 ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              title="Excel-style table view"
            >
              Table
            </button>
          </div>
        </form>

        {isLoading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            Loading companies...
          </div>
        ) : companies.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-gray-600">
            <Building2 className="w-10 h-10 mx-auto text-blue-600 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              {searchQuery ? `No companies matching "${searchQuery}"` : 'No companies on file'}
            </h2>
            <p className="text-sm">
              {searchQuery
                ? 'Try a different search term or clear the filter.'
                : 'Submit a new analysis and the company will show up here.'}
            </p>
          </div>
        ) : viewMode === 'table' ? (
          <EntityTableView
            rows={companies}
            csvFilename="companies.csv"
            onRowClick={(co) => navigate(`/companies/${(co as CompanyRecord).id}`)}
            columns={[
              { key: 'name', label: 'Name' },
              {
                key: 'industry',
                label: 'Industry',
                render: (c: CompanyRecord) =>
                  [c.industry, c.fundingStage].filter(Boolean).join(' • ') || '—',
              },
              {
                key: 'completion',
                label: 'Completion',
                align: 'right',
                render: (c: CompanyRecord) => `${c.research_completion_pct ?? 0}%`,
                csv: (c: CompanyRecord) => c.research_completion_pct ?? 0,
                // `key` is a display id, not a field name — without this the
                // table sorts on row.completion (undefined) and silently
                // no-ops, which is why Name/Industry sorted but this didn't.
                sortValue: (c: CompanyRecord) => c.research_completion_pct ?? null,
              },
              {
                key: 'rubric',
                label: 'Rubric',
                align: 'right',
                render: (c: CompanyRecord) =>
                  c.rubric_score !== undefined && c.rubric_score !== null
                    ? `${c.rubric_score} (${c.rubric_tier || ''})`
                    : '—',
                csv: (c: CompanyRecord) => c.rubric_score ?? '',
                sortValue: (c: CompanyRecord) => c.rubric_score ?? null,
              },
              {
                key: 'recommendation',
                label: 'Recommendation',
                render: (c: CompanyRecord) => c.recommendation || '—',
              },
              {
                key: 'lastAnalysis',
                label: 'Last reviewed',
                render: (c: CompanyRecord) =>
                  c.lastAnalysis ? new Date(c.lastAnalysis).toLocaleDateString() : '—',
              },
            ]}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {companies.map((company) => {
              const crs = crsMap[company.id];
              const mom = momentumMap[company.id];
              return (
              <div
                key={company.id}
                onClick={() => navigate(`/companies/${company.id}`)}
                className="bg-white border border-gray-200 rounded-xl p-6 space-y-3 cursor-pointer hover:border-blue-400 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-lg font-semibold text-gray-900">{company.name}</h2>
                      <DualScoreChip
                        completionPct={company.research_completion_pct ?? null}
                        rubricScore={company.rubric_score ?? null}
                        rubricTier={company.rubric_tier ?? null}
                      />
                    </div>
                    <p className="text-sm text-gray-600">
                      {[company.industry, company.fundingStage].filter(Boolean).join(' • ') || 'Stage TBD'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Last reviewed</p>
                    <p className="text-sm text-gray-700">{formatDate(company.lastAnalysis)}</p>
                  </div>
                </div>
                {(crs || mom) && (
                  <div className="flex flex-wrap items-center gap-2">
                    {crs && (
                      <Link
                        to={`/capital-readiness/scores/${crs.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-blue-200 bg-blue-50 rounded-full text-xs font-medium text-blue-700 hover:bg-blue-100"
                        title="Capital readiness score"
                      >
                        <span>CRS {Math.round(crs.overall_score)}</span>
                        <CRSTierBadge tier={crs.tier} size="sm" />
                      </Link>
                    )}
                    {mom && (
                      <Link to={`/momentum/startup/${company.id}`} onClick={(e) => e.stopPropagation()}>
                        <MomentumBadge tier={mom.momentum_tier} size="sm" />
                      </Link>
                    )}
                  </div>
                )}

                <p className="text-sm text-gray-700 leading-relaxed">
                  {(() => {
                    // Fallback chain: hand-curated summary → AI-generated
                    // executive summary → free-form description. Truncate
                    // to ~200 chars so the card height stays predictable.
                    const raw =
                      company.summary ||
                      company.executive_summary ||
                      company.description ||
                      '';
                    const text = raw.trim();
                    if (text) {
                      return text.length > 200 ? `${text.slice(0, 200)}…` : text;
                    }
                    // Last resort — synthesize a one-liner from structured
                    // fields so the card never renders the empty placeholder.
                    const parts = [company.industry, company.fundingStage].filter(Boolean);
                    return parts.length
                      ? `${parts.join(' · ')} — full summary pending research depth.`
                      : 'Summary will appear here once research lands.';
                  })()}
                </p>

                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>
                    Recommendation:{' '}
                    <span className="font-medium text-gray-900">{company.recommendation ? company.recommendation.replace('_', ' ').toUpperCase() : 'TBD'}</span>
                  </span>
                  <span title="Analysis-engine score (separate from CRS — needs Claude analysis to populate)">
                    Analysis: {company.score ?? '—'}
                  </span>
                </div>

                {/* Research-completion rubric progress bar */}
                {(() => {
                  const pct = company.research_completion_pct ?? 0;
                  const barColor =
                    pct >= 80 ? 'bg-green-500' :
                    pct >= 50 ? 'bg-blue-500' :
                    pct >= 20 ? 'bg-amber-500' : 'bg-gray-300';
                  return (
                    <div title="Research completion (rubric of fields populated)">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>Research completion</span>
                        <span className="font-medium text-gray-700">{pct}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })()}

                {/* Assessment freshness — green/amber/red badge based on
                    last_researched_at age. */}
                {(() => {
                  const f = freshnessFor((company as any).last_researched_at);
                  return (
                    <div className={`text-[11px] font-medium border rounded px-2 py-1 ${f.color}`}>
                      {f.label}
                    </div>
                  );
                })()}

                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>
                    {company.documents && company.documents.length > 0
                      ? `${company.documents.length} supporting document${company.documents.length > 1 ? 's' : ''}`
                      : 'No documents uploaded yet'}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleResearchOne(company.id); }}
                    disabled={researchingId === company.id}
                    className="inline-flex items-center px-2.5 py-1 border border-purple-200 bg-purple-50 rounded text-xs font-medium text-purple-700 hover:bg-purple-100 disabled:opacity-50"
                    title="Run the deep-dive research pipeline for this company"
                  >
                    {researchingId === company.id ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Search className="w-3 h-3 mr-1" />
                    )}
                    Research
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
      <ManageGroupsModal
        open={manageGroupsOpen}
        onClose={() => setManageGroupsOpen(false)}
        onChanged={async () => {
          // Refresh the group filter dropdown after any change.
          const r = await dataService.fetchCompanyGroups();
          if (r.data) setGroupOptions(r.data);
        }}
      />
    </DashboardLayout>
  );
};

export default CompaniesPage;
